'use client';

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { createPortal } from "react-dom";

function FloatingBackHomeButton({ onClick }: { onClick: () => void }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return createPortal(
    <button
      type="button"
      onClick={onClick}
      className="fixed bottom-20 right-4 z-[9999] flex items-center gap-2 rounded-full bg-[#3B7C3C] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-white shadow-lg hover:bg-[#316834] md:bottom-6 md:right-6 md:px-4 md:py-2 md:text-xs"
      aria-label="Volver al home"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="h-4 w-4"
        aria-hidden="true"
      >
        <path d="M12 3l9 8h-3v10h-4V15H10v6H6V11H3l9-8z" />
      </svg>
      Volver al home
    </button>,
    document.body,
  );
}

export default function ConoceMasPage() {
  const router = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const goToLogin = () => {
    router.push("/login");
  };

  const goToMaterials = () => {
    router.push("/materiales-educativos");
  };

  return (
    <div className="min-h-screen bg-[#C0E58A] text-[#1F2933]">
      <FloatingBackHomeButton onClick={() => router.push("/")} />
      {/* Navbar igual al de la página principal */}
      <header className="bg-[#9B4BB9] text-white shadow-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 md:px-6">
          {/* Logo / Marca */}
          <div className="flex items-center gap-3">
            <Link href="/" className="flex items-center gap-3">
              <Image
                src="/images/Logo-loop-gp-escuelas.gif"
                alt="Greenpeace en las escuelas"
                width={140}
                height={40}
                priority
                unoptimized
                className="h-auto w-[140px] object-contain md:w-[160px]"
              />
            </Link>
          </div>

          {/* Navegación desktop (igual que en la home) */}
          <nav className="hidden items-center gap-8 text-sm font-medium md:flex">
            <button
              type="button"
              onClick={() => router.push("/conoce-mas#programa")}
              className="pb-1 hover:border-b-2 hover:border-white/70"
            >
              EL PROGRAMA
            </button>
            <button
              type="button"
              onClick={goToMaterials}
              className="pb-1 hover:border-b-2 hover:border-white/70"
            >
              MATERIALES EDUCATIVOS
            </button>
            <a
              href="#contacto"
              className="pb-1 hover:border-b-2 hover:border-white/70"
            >
              CONTACTO
            </a>
          </nav>

          {/* CTA + menú móvil */}
          <div className="flex items-center gap-3">
            {/* Icono login */}
            <button
              type="button"
              onClick={goToLogin}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-xs font-semibold hover:bg-white/20"
              aria-label="Iniciar sesión"
            >
              🔒
            </button>

            <a
              href="https://app.greenpeace.org.ar/coupon/regular/forms/registration?app=escuelasm"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden rounded-full bg-[#FF8A3D] px-4 py-2 text-xs font-semibold uppercase tracking-wide shadow-md hover:bg-[#ff7a20] md:inline-flex"
            >
              DONÁ →
            </a>

            {/* Botón hamburguesa solo en mobile */}
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-white/40 md:hidden"
              aria-label="Abrir menú de navegación"
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              <span className="sr-only">Toggle navigation</span>
              <div className="space-y-1.5">
                <span className="block h-0.5 w-5 bg-white" />
                <span className="block h-0.5 w-5 bg-white" />
                <span className="block h-0.5 w-5 bg-white" />
              </div>
            </button>
          </div>
        </div>

        {/* Menú desplegable móvil */}
        {isMenuOpen && (
          <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 pb-4 text-sm font-medium md:hidden">
            <button
              className="w-full rounded-md bg-white/10 px-3 py-2 text-left hover:bg-white/15"
              type="button"
              onClick={() => {
                setIsMenuOpen(false);
                router.push("/conoce-mas#programa");
              }}
            >
              EL PROGRAMA
            </button>
            <button
              className="w-full rounded-md bg-white/10 px-3 py-2 text-left hover:bg-white/15"
              type="button"
              onClick={() => {
                setIsMenuOpen(false);
                goToMaterials();
              }}
            >
              MATERIALES EDUCATIVOS
            </button>
            <a
              href="#contacto"
              className="w-full rounded-md bg-white/10 px-3 py-2 text-left hover:bg-white/15"
              onClick={() => setIsMenuOpen(false)}
            >
              CONTACTO
            </a>
            <a
              href="https://app.greenpeace.org.ar/coupon/regular/forms/registration?app=escuelasm"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 block w-full rounded-full bg-[#FF8A3D] px-4 py-2 text-center text-xs font-semibold uppercase tracking-wide text-white shadow-md hover:bg-[#ff7a20]"
            >
              DONÁ →
            </a>
          </div>
        )}
      </header>

      <main className="w-full px-0 pt-0 pb-0">
        {/* Banner a todo el ancho */}
        <div className="mb-8 w-full">
          {/* Mobile: zoom + recorte */}
          <div className="relative h-[220px] w-full overflow-hidden md:hidden">
            <Image
              src="/images/capacitacion.jpg"
              alt="Conocé más sobre Greenpeace"
              fill
              sizes="100vw"
              className="conoce-banner-mobile object-cover shadow-md transform-gpu scale-[1.45] object-[50%_82%]"
            />
          </div>

          {/* Desktop: sin zoom (imagen normal) */}
          <div className="relative hidden w-full md:block">
            <Image
              src="/images/capacitacion.jpg"
              alt="Conocé más sobre Greenpeace"
              width={1920}
              height={500}
              className="h-auto w-full object-cover shadow-md md:max-h-[540px] lg:max-h-[580px] object-[50%_70%]"
            />
          </div>
        </div>

        {/* Primera sección: texto + foto en dos columnas */}
        <section className="mx-auto flex max-w-6xl flex-col items-center gap-10 px-4 pt-8 text-sm leading-relaxed md:flex-row md:items-center md:pt-12 md:text-base">
          {/* Columna izquierda: texto principal */}
          <div className="w-full md:w-1/2">
            <p className="text-justify">
              En <span className="font-semibold">GREENPEACE</span> llevamos más de 50 años trabajando en la
              defensa del ambiente con distintas acciones: campañas creativas de
              visibilización de conflictos ambientales; difusión de información a
              través de nuestras redes y blog; participación activa en foros y
              encuentros ambientales; presión para que se sancionen leyes y regulaciones
              que protejan nuestra biodiversidad; denuncias a empresas y grupos que
              atentan contra la naturaleza, entre muchas otras.
            </p>
          </div>

          {/* Columna derecha: foto grande */}
          <div className="w-full md:w-1/2">
            <Image
              src="/images/auditorio.png"
              alt="Actividades de Greenpeace en escuelas"
              width={800}
              height={500}
              className="h-auto w-full rounded-2xl object-cover shadow-md"
            />
          </div>
        </section>

        {/* Segunda sección: texto + imagen */}
        <section className="mx-auto mt-12 flex max-w-6xl flex-col-reverse items-center gap-10 px-4 text-sm leading-relaxed md:mt-16 md:flex-row md:items-center md:text-base">
          {/* Columna izquierda: imagen */}
          <div className="w-full md:w-1/2">
            <Image
              src="/images/greenpeace-teaching.png"
              alt="Educación ambiental con Greenpeace"
              width={800}
              height={500}
              className="h-auto w-full max-h-[300px] rounded-2xl object-cover shadow-md md:max-h-[350px]"
            />
          </div>

          {/* Columna derecha: texto */}
          <div className="w-full md:w-1/2">
            <p className="text-justify">
              Si bien todo esto es muy importante y valioso, sabemos que también es necesario un{" "}
              <span className="font-semibold">cambio profundo de valores</span> en nuestras sociedades. Ahora más
              que nunca es importante acudir a la educación ambiental para cambiar la forma en la que nos
              relacionamos con la naturaleza y para seguir informando sobre la importancia del cuidado de
              nuestro planeta.
            </p>
          </div>
        </section>

        {/* Tercera sección: texto + imagen */}
        <section className="mx-auto mt-12 flex max-w-6xl flex-col items-center gap-10 px-4 text-sm leading-relaxed md:mt-16 md:flex-row md:items-center md:text-base">
          {/* Columna izquierda: texto */}
          <div className="w-full md:w-1/2">
            <p className="text-justify">
              La escuela aparece como un espacio privilegiado para llevar adelante esta tarea de formar a las
              nuevas generaciones en actitudes de cuidado, respeto y protección del ambiente. Creemos que la
              escuela tiene un enorme potencial para este desafío al ser un espacio de construcción del
              conocimiento, de participación colectiva, de solidaridad y compromiso.
            </p>
          </div>

          {/* Columna derecha: imagen */}
          <div className="w-full md:w-1/2">
            <Image
              src="/images/classroom.png"
              alt="Aula y educación ambiental"
              width={800}
              height={500}
              className="h-auto w-full rounded-2xl object-cover shadow-md"
            />
          </div>
        </section>

        {/* Sección: El programa */}
        <section
          id="programa"
          className="mt-16 w-full pt-12 pb-6 text-white md:mt-20 md:pt-16 md:pb-8"
          style={{
            backgroundImage: "url('/images/2-FondosWeb-GPEsc.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <h2 className="text-center text-sm font-extrabold uppercase tracking-[0.2em] text-white">
              EL PROGRAMA
            </h2>

            <div className="mt-8 grid gap-10 text-sm leading-relaxed text-white/95 md:grid-cols-2 md:text-base">
              <p className="text-justify">
                Greenpeace en las escuelas es un programa educativo dirigido a educadores y educadoras de todo el
                país, que tiene el objetivo de difundir y acercar información, materiales y recursos educativos que
                permitan la concientización acerca de los conflictos y problemas ambientales que estamos
                atravesando a nivel global, regional y local. Con todo esto, buscamos que los niños, niñas y
                adolescentes se interioricen con estas problemáticas y sumen sus voces e ideas en la búsqueda de
                alternativas y soluciones.
              </p>
              <p className="text-justify">
                Greenpeace en las escuelas es un programa que funciona como espacio de encuentro con la comunidad
                educativa, en la que desde Greenpeace buscamos promover, acompañar y potenciar las experiencias y
                proyectos de educación ambiental en las escuelas.
              </p>
            </div>
          </div>
        </section>

        {/* Sección: ¿Qué ofrecemos? */}
        <section className="w-full bg-[#E9F8D7]/60 pt-12 pb-12 md:pt-16 md:pb-16">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <h2 className="text-center text-sm font-extrabold uppercase tracking-[0.2em] text-[#1F2933] md:text-base">
              ¿Qué ofrece Greenpeace en las escuelas?
            </h2>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <article className="flex gap-4 rounded-2xl bg-white/95 p-5 shadow-md transition-shadow hover:shadow-lg">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#7CCB4D]/20 text-2xl" aria-hidden>
                  🗂️
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-relaxed text-[#1F2933] text-justify">
                    Web de Greenpeace en las Escuelas con mucha información, notas semanales, ideas, recursos, entrevistas, y otros materiales para hacer educación ambiental en el aula. Con secciones diferenciadas para docentes, jóvenes y niños.
                  </p>
                </div>
              </article>
              <article className="flex gap-4 rounded-2xl bg-white/95 p-5 shadow-md transition-shadow hover:shadow-lg">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#7CCB4D]/20 text-2xl" aria-hidden>
                  📚
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-relaxed text-[#1F2933] text-justify">
                    Materiales educativos con orientaciones para trabajar temáticas ambientales actuales en el aula.
                  </p>
                </div>
              </article>
              <article className="flex gap-4 rounded-2xl bg-white/95 p-5 shadow-md transition-shadow hover:shadow-lg">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#7CCB4D]/20 text-2xl" aria-hidden>
                  🎬
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-relaxed text-[#1F2933] text-justify">
                    Contenido audiovisual para juventudes: campañas ambientales y documentales realizados por Greenpeace junto a personalidades reconocidas como Rusherking, Natalie Perez y el Chino Darín, y muchos otros.
                  </p>
                </div>
              </article>
              <article className="flex gap-4 rounded-2xl bg-white/95 p-5 shadow-md transition-shadow hover:shadow-lg">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#7CCB4D]/20 text-2xl" aria-hidden>
                  🗣️
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-relaxed text-[#1F2933] text-justify">
                    Charlas en escuelas llevadas adelante por nuestro equipo de talleristas, voluntarios y voluntarias.
                  </p>
                </div>
              </article>
              <article className="flex gap-4 rounded-2xl bg-white/95 p-5 shadow-md transition-shadow hover:shadow-lg">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#7CCB4D]/20 text-2xl" aria-hidden>
                  📧
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-relaxed text-[#1F2933] text-justify">
                    Newsletter con noticias ambientales para docentes y educadores, y materiales para trabajar las efemérides en clase.
                  </p>
                </div>
              </article>
              <article className="flex gap-4 rounded-2xl bg-white/95 p-5 shadow-md transition-shadow hover:shadow-lg">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#7CCB4D]/20 text-2xl" aria-hidden>
                  🎬
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-relaxed text-[#1F2933] text-justify">
                    Contenido audiovisual infantil: La serie &quot;Lina y los amigos del arcoíris&quot; de 8 capítulos para abordar distintos conflictos ambientales con sus estudiantes, junto con actividades para el aula, videoclips, podcast, etc. Y más videos de Greenpeace Kids.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* Sección: Docentes y Educadores */}
        <section className="mx-auto mt-12 flex max-w-6xl flex-col items-center gap-10 px-4 md:mt-16 md:flex-row md:items-center">
          {/* Columna izquierda: imagen */}
          <div className="w-full md:w-1/2">
            <Image
              src="/images/donacion.png"
              alt="Docentes y educadores en actividades ambientales"
              width={800}
              height={500}
              className="h-auto w-full rounded-2xl object-cover shadow-md"
            />
          </div>

          {/* Columna derecha: título, texto y CTA */}
          <div className="flex w-full flex-col gap-6 md:w-1/2">
            <h2 className="text-left text-sm font-extrabold uppercase tracking-[0.2em] text-[#1F2933] md:text-base">
              Docentes y Educadores
            </h2>
            <p className="text-justify text-sm leading-relaxed text-[#1F2933] md:text-base">
              Materiales educativos con orientaciones para trabajar temáticas ambientales actuales en el aula.{" "}
              <span className="font-semibold">Guías, calendario ambiental, e-books, actividades, revistas infantiles</span> y mucho más.
            </p>
            <Link
              href="/materiales-educativos"
              className="animate-cta-pulse inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-[#7CCB4D] to-[#5FAE34] px-6 py-3.5 text-center text-sm font-bold uppercase tracking-wide text-white shadow-md shadow-[#1F2933]/15 transition hover:scale-105 hover:from-[#8AD75A] hover:to-[#6ab83f] hover:shadow-lg active:scale-100 md:w-auto"
            >
              Registrate o ingresá a tu cuenta y accedé a todo el material
            </Link>
          </div>
        </section>
      </main>

      {/* Footer reutilizado de la página principal / materiales */}
      <footer
        id="contacto"
        className="relative bg-cover bg-center bg-no-repeat text-[#1F2933]"
        style={{
          backgroundColor: "#9ACD32",
          backgroundImage: "linear-gradient(to top, rgba(30,30,30,0.22) 0%, transparent 22%)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-12">
          <div className="grid gap-8 md:grid-cols-2 md:gap-12 md:items-center">
            <div className="flex flex-col items-start gap-4 md:order-2">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#1F2933]">
                Contacto
              </h3>
              <div className="h-px w-16 bg-[#1F2933]/40" aria-hidden />
              <p className="text-sm leading-relaxed text-[#1F2933]/95">
                Para contactarte con nosotros escribinos por las siguientes vías de comunicación:
              </p>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="https://wa.me/5491171206317"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-medium text-[#1F2933] hover:underline"
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden>
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </span>
                    +54 9 11 7120 6317
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:escuelas@greenpeace.org.ar"
                    className="inline-flex items-center gap-2 font-medium text-[#1F2933] hover:underline"
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden>
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </span>
                    escuelas@greenpeace.org.ar
                  </a>
                </li>
              </ul>
            </div>
            <div className="flex flex-col items-center gap-4 md:order-1 md:items-start md:justify-center">
              <a
                href="https://www.greenpeace.org/argentina/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1F2933]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#9ACD32]"
                aria-label="Fundación Greenpeace Argentina (sitio web)"
              >
                <Image
                  src="/images/gp-logo-blanco.svg"
                  alt="Greenpeace Argentina"
                  width={160}
                  height={48}
                  className="h-auto w-36 object-contain md:w-40"
                />
              </a>
              <p className="text-xs text-[#1F2933]/90">
                © 2026 Greenpeace Argentina. Todos los derechos reservados.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

